import React, { createRef, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { determineChipSelected, generateFieldChips } from 'app/components/AmWorkDesk/PlacementsDesk/filterHelper';
import { selectFilters, selectGridState } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { ChipsContainer } from 'app/ComponentLibrary/Filter';
import { selectOrderCandidateMatchChips } from './store/orderCandidateMatch.selectors';
import { getOrderCandidateMatchActions } from './store/orderCandidateMatch.redux';
import { generateChips } from '@AMIEWEB/GlobalSearch/helper';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { makeStyles } from 'tss-react/mui';
import { instanceOfAggregateCandidate } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { instanceOfCandidateSkillSetFilter } from 'app/ComponentLibrary/Filter/CandidateSkillset';
import { deleteAdditionalSkillSetFilters, getFilteredSkillSets } from '@AMIEWEB/GlobalSearch/SearchResultsChips';
import { totalCountNodes } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const OrderCandidateMatchChips = props => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { setValue, getValues } = useFormContext();
  const chips = useSelector(selectOrderCandidateMatchChips);
  const appliedFilter = useSelector(selectFilters);
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const dispatch = useDispatch();

  const gridState = useSelector(selectGridState);
  const [showChipsCollapseBtn, setShowChipsCollapseBtn] = useState(false);
  const [checked, setChecked] = React.useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const handleChange = () => {
    setChecked(prev => !prev);
  };

  useEffect(() => {
    const chipsData = Object.keys(chips).some(key => {
      const value = chips[key];
      if (Array.isArray(value) && value.length > 0) {
        return true;
      }
      if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) {
        return true;
      }
      return false;
    });
    setShowChipsCollapseBtn(chipsData);
  }, [chips]);

  const getNewSelection = (key, chip) => ({
    ...appliedFilter?.selectedChipFilters,
    [key]: determineChipSelected(
      chip,
      appliedFilter?.selectedChipFilters ? appliedFilter?.selectedChipFilters[key] : undefined,
    ),
  });

  const handleChipDelete = (key, chip) => {
    const fieldValues = getValues(key) ?? getValues(chip.value);
    let newFieldValues: any = null;

    if(instanceOfCandidateSkillSetFilter(fieldValues)){
      const skillsets = getFilteredSkillSets(fieldValues?.skillsets, chip);
      const skillsetsCount = totalCountNodes({
        isVerifiedOnly: false,
        operation: null,
        allowableMismatch: false,
        skillsets: skillsets,
      });
      newFieldValues = {
        isVerifiedOnly: deleteAdditionalSkillSetFilters('isVerifiedOnly', chip, skillsetsCount) ? false : fieldValues?.isVerifiedOnly,
        operation: deleteAdditionalSkillSetFilters('operation', chip, skillsetsCount) ? '' : fieldValues?.operation,
        allowableMismatch: deleteAdditionalSkillSetFilters('allowableMismatch', chip, skillsetsCount)
          ? false
          : fieldValues?.allowableMismatch,
        skillsets: skillsets,
      };
    }
    else if (instanceOfAggregateCandidate(fieldValues)) {
      newFieldValues = {
        ...fieldValues,
        [chip?.value?.key]: !Array.isArray(fieldValues[chip?.value?.key])
          ? null
          : fieldValues[chip?.value?.key]?.filter(x => x.value !== chip?.value?.value?.value),
      };
    } else {
      if (fieldValues && fieldValues.type && fieldValues.type === 'NestedFilter') {
        const data = generateFieldChips(fieldValues);
        const index = data.findIndex(x => x.label === chip.label);
        newFieldValues = {
          ...fieldValues,
          value: fieldValues.value.filter((item, id) => index !== id),
        };
      } else {
        newFieldValues = Array.isArray(fieldValues)
          ? fieldValues.filter(item => item.value !== chip.value.value ?? chip?.value)
          : null;
      }
    }
    setValue(key, newFieldValues, { shouldDirty: true });
    if (chip.isSelected || props.disableFormSubmit) {
      const newSelections = getNewSelection(key, chip);
      props.onApplyFilter(
        {
          ...getValues(),
          [key]: Array.isArray(fieldValues)
            ? [...(newFieldValues || [])]
            : (fieldValues.type && fieldValues.type === 'NestedFilter') || instanceOfAggregateCandidate(newFieldValues)
            ? newFieldValues
            : null,
        },
        newSelections,
      );
    } else hiddenSubmitButton.current?.click();
  };

  /** Restricted to call onApplyfilter directly -
   * observes not to remove existing filter and chips */
  const handleChipClick = (key, chip) => {
    const fieldValues = getValues(key);
    /**Check to avoid click on single valued fields/chips */
    if (!Array.isArray(fieldValues)) {
      if (!instanceOfAggregateCandidate(fieldValues) && !instanceOfCandidateSkillSetFilter(fieldValues)) return;
      else if (chip?.value?.key && !Array.isArray(fieldValues[chip.value.key])) return;
    }
    /**
     * Below functionalty are limited to type ISelection
     * Need to be updated for dates and single selection
     */
    const newChipSelection = getNewSelection(key, chip);
    props.onApplyFilter(getValues(), newChipSelection);
  };

  useEffect(() => {
    if (gridState.gridTag === 'CandidateMatch') {
      appliedFilter?.filters &&
        dispatch(
          getOrderCandidateMatchActions.setChips({
            ...chips,
            ...generateChips(appliedFilter?.filters, t, SearchType.candidate, false, appliedFilter.selectedChipFilters),
          }),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilter?.filters]);

  return (
    <>
      {showChipsCollapseBtn ? (
        <Grid container spacing={2}>
          <Grid item xs={11}>
            <Collapse in={checked} collapsedSize={38}>
              <div ref={divRef}>
                <ChipsContainer
                  chips={chips}
                  onClick={handleChipClick}
                  onDelete={handleChipDelete}
                  isCollapse={!checked}
                />
                <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
              </div>
            </Collapse>
          </Grid>
          <Grid item xs={1} className={classes.fillterCollapse}>
            {(divRef?.current?.clientHeight ?? 0) > 38 && (
              <SearchPageIcons KeyboardArrowIcon onAdvancedChipsClick={() => handleChange()} checked={checked} />
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          <ChipsContainer chips={chips} onClick={handleChipClick} onDelete={handleChipDelete} />
          <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
        </>
      )}
    </>
  );
};

export default OrderCandidateMatchChips;
